import Cookies from 'js-cookie';

export class CookieService {
	/**
	 * 设置 cookie
	 * @param key 键名
	 * @param value 值
	 * @param options 配置选项
	 */
	static set(key: string, value: any, options?: Cookies.CookieAttributes): void {
		Cookies.set(key, value, options);
	}

	/**
	 * 获取 cookie
	 * @param key 键名
	 */
	static get(key: string): string | undefined {
		return Cookies.get(key);
	}

	/**
	 * 获取所有 cookie
	 */
	static getAll(): { [key: string]: string } {
		return Cookies.get();
	}

	/**
	 * 删除 cookie
	 * @param key 键名
	 * @param options 配置选项 { path: '', domain: '' }
	 */
	static remove(key: string, options?: Cookies.CookieAttributes): void {
		Cookies.remove(key, options);
	}

	/**
	 * 检查 cookie 是否存在
	 * @param key 键名
	 */
	static has(key: string): boolean {
		return this.get(key) !== undefined;
	}

	/**
	 * 带过期时间的设置方法
	 * @param key 键名
	 * @param value 值
	 * @param expires 过期时间（数值）
	 * @param unit 时间单位
	 * @param path cookie 路径
	 */
	static setWithExpiry(key: string, value: any, expires: number, unit: 'days' | 'hours' | 'minutes' | 'seconds' = 'days', path: string = '/'): void {
		const date = new Date();
		switch (unit) {
			case 'days':
				date.setTime(date.getTime() + expires * 24 * 60 * 60 * 1000);
				break;
			case 'hours':
				date.setTime(date.getTime() + expires * 60 * 60 * 1000);
				break;
			case 'minutes':
				date.setTime(date.getTime() + expires * 60 * 1000);
				break;
			case 'seconds':
				date.setTime(date.getTime() + expires * 1000);
				break;
		}
		this.set(key, value, { expires: date, path });
	}
}

export class LocalStorageService {
	static set(key: string, value: any, expiredAt?: number, expiredUnit: string = 'milliseconds'): void {
		const data = {
			value,
			expiredAt: expiredAt ? this.calculateExpiredTime(expiredAt, expiredUnit) : null,
		};
		localStorage.setItem(key, JSON.stringify(data));
	}

	static get(key: string): any {
		const item = localStorage.getItem(key);
		if (!item) return null;

		const data = JSON.parse(item);
		if (data.expiredAt && Date.now() > data.expiredAt) {
			this.remove(key);
			return null;
		}
		return data.value;
	}

	static remove(key: string): void {
		localStorage.removeItem(key);
	}

	static clear(): void {
		localStorage.clear();
	}

	static calculateExpiredTime(time: number, unit: string): number {
		switch (unit) {
			case 'seconds':
				return Date.now() + time * 1000;
			case 'minutes':
				return Date.now() + time * 60 * 1000;
			case 'hours':
				return Date.now() + time * 60 * 60 * 1000;
			case 'days':
				return Date.now() + time * 24 * 60 * 60 * 1000;
			default:
				return Date.now() + time; // 默认毫秒
		}
	}
}

export class SessionStorageService {
  /**
   * 设置 sessionStorage 项
   * @param key 键名
   * @param value 值
   */
  static set<T>(key: string, value: T): void {
    sessionStorage.setItem(key, JSON.stringify(value))
  }

  /**
   * 获取 sessionStorage 项
   * @param key 键名
   * @param defaultValue 默认值（可选）
   */
  static get<T>(key: string, defaultValue: T | null = null): T | null {
    const item = sessionStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  }

  /**
   * 移除指定 sessionStorage 项
   * @param key 键名
   */
  static remove(key: string): void {
    sessionStorage.removeItem(key)
  }

  /**
   * 清空所有 sessionStorage
   */
  static clear(): void {
    sessionStorage.clear()
  }

  /**
   * 检查是否存在指定键
   * @param key 键名
   */
  static has(key: string): boolean {
    return sessionStorage.getItem(key) !== null
  }

  /**
   * 获取所有键名
   */
  static keys(): string[] {
    return Object.keys(sessionStorage)
  }

}
